// LiveKit API service

/**
 * Generate a LiveKit token for a specific agent
 * @param {string} agentId - The ID of the agent
 * @param {string} roomName - Optional room name (will be generated if not provided)
 * @param {boolean} isPlugnPlay - Whether this is a Plug and Play agent
 * @returns {Promise<{token: string, roomName: string}>}
 */
export const generateLiveKitToken = async (agentId, roomName = null, isPlugnPlay = false) => {
  try {
    // Make sure this URL is correct and matches your backend
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    // Use different endpoint for Plug and Play agents
    const endpoint = isPlugnPlay ? '/api/plugnplay/livekit/token' : '/api/livekit/token';
    
    console.log('Generating LiveKit token for agent:', agentId, 'isPlugnPlay:', isPlugnPlay);
    console.log('Using endpoint:', `${apiUrl}${endpoint}`);
    
    // Get the correct token key (check both possible keys)
    const accessToken = localStorage.getItem('access_token') || localStorage.getItem('token');
    
    if (!accessToken) {
      console.error('❌ No access token found in localStorage');
      throw new Error('Not authenticated - please log in again');
    }
    
    console.log('Using access token:', accessToken ? `${accessToken.substring(0, 20)}...` : 'none');
    
    // First check if the server is accessible with a simple preflight request
    try {
      const preflightResponse = await fetch(`${apiUrl}${endpoint}`, {
        method: 'OPTIONS',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('Preflight response status:', preflightResponse.status);
    } catch (err) {
      console.warn('Preflight check failed:', err);
    }
    
    // Now make the actual request
    const response = await fetch(`${apiUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        agent_id: agentId,
        room_name: roomName
      })
    });

    console.log('LiveKit token response status:', response.status);
    console.log('LiveKit token response headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('LiveKit token error response:', errorText);
      try {
        const errorData = JSON.parse(errorText);
        
        // Check if this is an insufficient balance error
        if (response.status === 402 && errorData.error === "Insufficient balance") {
          // Create an error object with all the balance information
          const insufficientBalanceError = new Error("Insufficient balance");
          insufficientBalanceError.status = 402;
          insufficientBalanceError.details = errorData;
          insufficientBalanceError.isInsufficientBalance = true;
          throw insufficientBalanceError;
        }
        
        throw new Error(errorData.error || `Failed with status: ${response.status}`);
      } catch (e) {
        // If it's already our custom error, rethrow it
        if (e.isInsufficientBalance) {
          throw e;
        }
        throw new Error(`Failed with status: ${response.status} - ${errorText}`);
      }
    }

    const data = await response.json();
    console.log('Successfully generated LiveKit token:', {
      hasToken: !!data.token,
      hasServerUrl: !!data.server_url,
      roomName: data.room_name
    });
    return data;
  } catch (error) {
    console.error('Error generating LiveKit token:', error);
    throw error;
  }
}; 